import { useEffect, useRef } from 'react'
import { clinicalGuidelines } from '../utils/guidelines'
import './GuidelinesFlyout.css'

interface GuidelinesFlyoutProps {
  isOpen: boolean
  onClose: () => void
  buttonRef: React.RefObject<HTMLButtonElement>
}

export function GuidelinesFlyout({ isOpen, onClose, buttonRef }: GuidelinesFlyoutProps) {
  const flyoutRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        flyoutRef.current &&
        !flyoutRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, buttonRef])

  if (!isOpen) return null

  return (
    <div ref={flyoutRef} className="guidelines-flyout" role="menu" aria-label="Clinical guidelines">
      <div className="guidelines-flyout-header">
        <h3>Clinical Guidelines</h3>
      </div>
      <div className="guidelines-flyout-body">
        <ul className="guidelines-list">
          {clinicalGuidelines.map((guideline, index) => (
            <li key={index} role="none">
              <a
                href={guideline.url}
                target="_blank"
                rel="noopener noreferrer"
                className="guidelines-link"
                role="menuitem"
                onClick={onClose}
              >
                {guideline.title}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="guidelines-link-icon"
                  aria-hidden="true"
                >
                  <path
                    d="M6 12L10 8L6 4M10 4H14V8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
